import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CustomPagination from 'app/widgets/Shared/Pagination';
import superadminServices from 'app/services/superadmin-services';
import {
  ActionIconButton,
  ResponsiveDataTable,
  StandardFiltersBar,
  StandardPageHeader,
  StandardSearchField,
} from 'app/shared/ui';

const STATE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'creating_organization', label: 'Creando organización' },
  { value: 'creating_store', label: 'Creando local' },
  { value: 'creating_devices', label: 'Creando dispositivos' },
  { value: 'creating_user', label: 'Creando usuario' },
  { value: 'creating_dashboard', label: 'Creando dashboard' },
  { value: 'deployed', label: 'Desplegada' },
  { value: 'notified', label: 'Notificada' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'ended', label: 'Detenida' },
  { value: 'error', label: 'Error' },
];

const LEAD_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'draft', label: 'En progreso' },
  { value: 'completed', label: 'Completado' },
  { value: 'abandoned', label: 'Abandonado' },
  { value: 'blocked_existing_user', label: 'Bloqueado por usuario existente' },
  { value: 'blocked_existing_demo', label: 'Bloqueado por demo existente' },
];

const ACTIVE_STATES = ['deployed', 'notified', 'in_progress'];

const stateLabel = (value) => STATE_OPTIONS.find((option) => option.value === value)?.label || value || '-';
const leadStatusLabel = (value) => LEAD_STATUS_OPTIONS.find((option) => option.value === value)?.label || value || '-';

const stateColor = (value) => {
  if (ACTIVE_STATES.includes(value)) return 'success';
  if (value === 'ended') return 'default';
  if (value === 'error') return 'error';
  return 'warning';
};

const leadStatusColor = (value) => {
  if (value === 'completed') return 'success';
  if (value === 'abandoned') return 'warning';
  if (value === 'blocked_existing_user' || value === 'blocked_existing_demo') return 'info';
  return 'default';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString();
};

const DemoRequestsIndex = () => {
  const [records, setRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [stoppingId, setStoppingId] = useState(null);

  const params = useMemo(() => {
    const parts = [`page=${currentPage}`];
    if (searchQuery.trim()) parts.push(`q=${encodeURIComponent(searchQuery.trim())}`);
    if (stateFilter) parts.push(`${activeTab === 'leads' ? 'status' : 'state'}=${encodeURIComponent(stateFilter)}`);
    return `?${parts.join('&')}`;
  }, [activeTab, currentPage, searchQuery, stateFilter]);

  const loadRecords = useCallback(() => {
    setLoading(true);
    const request = activeTab === 'leads'
      ? superadminServices.getDemoRequestLeads(params)
      : superadminServices.getDemoRequests(params);

    request
      .then((resp) => {
        setRecords(resp?.data || []);
        setTotalPages(resp?.meta?.total_pages || 1);
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar la información de demos.' }))
      .finally(() => setLoading(false));
  }, [activeTab, params]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const stopDemoRequest = (record) => {
    if (!record?.id || !window.confirm(`¿Detener demo request ${record.company_name || record.id}?`)) return;

    setStoppingId(record.id);
    superadminServices.stopDemoRequest(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Demo request detenido.' });
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo detener el demo request.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setStoppingId(null));
  };

  const renderRowActions = (record) => (
    <ActionIconButton
      size="small"
      title="Detener demo"
      disabled={record.state === 'ended' || stoppingId === record.id}
      onClick={() => stopDemoRequest(record)}
    >
      <StopCircleOutlinedIcon fontSize="small" color={record.state === 'ended' ? 'disabled' : 'error'} />
    </ActionIconButton>
  );

  const columns = [
    {
      key: 'company',
      label: 'Empresa',
      render: (record) => record?.company?.name || record?.company_name || '-',
    },
    {
      key: 'state',
      label: 'Estado',
      render: (record) => (
        <Chip size="small" color={stateColor(record.state)} label={stateLabel(record.state)} />
      ),
    },
    {
      key: 'retention',
      label: 'Retención',
      render: (record) => `${record?.company?.device_metrics_retention_days || 30} días`,
    },
    {
      key: 'email',
      label: 'Email',
      render: (record) => record.email || '-',
    },
    {
      key: 'store',
      label: 'Local',
      render: (record) => record?.store?.name || record.location_name || '-',
    },
    {
      key: 'created_at',
      label: 'Creado',
      render: (record) => formatDateTime(record.created_at),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderRowActions(record),
    },
  ];

  const leadColumns = [
    {
      key: 'company',
      label: 'Empresa',
      render: (record) => record.company_name || '-',
    },
    {
      key: 'status',
      label: 'Estado',
      render: (record) => (
        <Chip size="small" color={leadStatusColor(record.status)} label={leadStatusLabel(record.status)} />
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (record) => record.email || '-',
    },
    {
      key: 'phone',
      label: 'Teléfono',
      render: (record) => record.phone || '-',
    },
    {
      key: 'store',
      label: 'Local',
      render: (record) => record.location_name || '-',
    },
    {
      key: 'last_seen_at',
      label: 'Última actividad',
      render: (record) => formatDateTime(record.last_seen_at),
    },
    {
      key: 'demo_request',
      label: 'Demo',
      render: (record) => record?.demo_request?.id || '-',
    },
  ];

  const statusOptions = activeTab === 'leads' ? LEAD_STATUS_OPTIONS : STATE_OPTIONS;
  const displayColumns = activeTab === 'leads' ? leadColumns : columns;

  return (
    <Box>
      <StandardPageHeader title="Superadministración · Demo requests" />

      <Paper sx={{ mb: 2, px: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            setActiveTab(value);
            setCurrentPage(1);
            setStateFilter('');
            setRecords([]);
          }}
        >
          <Tab value="requests" label="Demos creadas" />
          <Tab value="leads" label="Leads del formulario" />
        </Tabs>
      </Paper>

      <StandardFiltersBar>
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Estado"
              value={stateFilter}
              onChange={(event) => {
                setCurrentPage(1);
                setStateFilter(event.target.value || '');
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={9}>
            <StandardSearchField
              fullWidth
              placeholder="Buscar por empresa, email, local o submission"
              onChange={(event) => {
                setCurrentPage(1);
                setSearchQuery(event.target.value || '');
              }}
            />
          </Grid>
        </Grid>
      </StandardFiltersBar>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={displayColumns}
            rows={records}
            rowKey="id"
            emptyMessage={activeTab === 'leads' ? 'No hay leads para mostrar.' : 'No hay demo requests para mostrar.'}
            mobileTitle={(record) => record?.company?.name || record?.company_name || '-'}
            mobileSubtitle={(record) => activeTab === 'leads' ? `${leadStatusLabel(record.status)} · ${record.email || '-'}` : `${stateLabel(record.state)} · ${record.email || '-'}`}
            renderRowActions={activeTab === 'leads' ? undefined : (record) => renderRowActions(record)}
          />
        )}
      </Paper>

      <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageSelect={setCurrentPage} />
    </Box>
  );
};

export default DemoRequestsIndex;
